import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LoadingScreen from './LoadingScreen';

/**
 * `requiredPermission` ("module:action", or an array of alternatives — see
 * below) is the parallel path for role: 'custom' — it never widens what a
 * fixed role can already reach, because a custom-role user is never a member
 * of `requiredRoles` in the first place. A route that should admit both a
 * fixed role AND a sufficiently-permissioned custom role passes both props;
 * either one being satisfied is enough.
 *
 * An array is for a route shared by more than one permission module (e.g.
 * the CRM/price-quote/invoice form, which is really the same page for all
 * three categories) — ANY one of them is enough, not all.
 */
const PrivateRoute = ({ requiredRoles = [], requiredPermission = null }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <LoadingScreen message="Authenticating..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (requiredRoles.length > 0 || requiredPermission) {
    const hasRequiredRole = requiredRoles.some(role => user?.role === role);
    const permissionsNeeded = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
    const hasRequiredPermission =
      Boolean(requiredPermission) &&
      user?.role === 'custom' &&
      permissionsNeeded.some((p) => (user?.permissions || []).includes(p));

    if (!hasRequiredRole && !hasRequiredPermission) {
      return <Navigate to="/unauthorized" replace />;
    }
  }
  return <Outlet />;
};

export default PrivateRoute;